/**
 * Which failures are worth trying again, and how long to wait between attempts.
 *
 * Check-in stations run on cellular data, where a drop of a second or two is ordinary. Retrying
 * absorbs those quietly, so a volunteer never learns the signal flickered.
 *
 * The rule that keeps this safe is that only methods safe to repeat are retried. A dropped
 * connection is reported identically whether the request died on the way out or on the way back,
 * so the browser cannot know whether the server processed it. Retrying a write on that ambiguity
 * is how a household gets registered twice or billed for a second text.
 */

/** How many additional attempts a retriable failure earns. */
export const MAX_RETRY_ATTEMPTS = 2;

/** Methods with no side effects, so repeating one cannot change what the server holds. */
const SAFE_METHODS = ['GET', 'HEAD'];

/**
 * Statuses that mean "nobody answered", as opposed to an answer we did not like.
 *
 * 0 is a request that never reached a server. 502/503/504 are the front door reporting that the
 * thing behind it did not answer — a container being swapped during a deploy, most often.
 */
const RETRIABLE_STATUSES = [0, 502, 503, 504];

/**
 * Whether a failed request should be attempted again.
 *
 * @param method The HTTP method of the request that failed.
 * @param status The status it failed with; 0 when the connection dropped.
 * @returns True when repeating the request is both safe and worth doing.
 */
export function isRetriable(method: string, status: number): boolean {
  return SAFE_METHODS.includes(method.toUpperCase()) && RETRIABLE_STATUSES.includes(status);
}

/**
 * How long to wait before the given attempt.
 *
 * Short and backing off: the first retry has to land inside the pause a volunteer already expects
 * from tapping a button, and the whole sequence has to finish well before waiting becomes worse
 * than being told the connection dropped.
 *
 * @param attempt The attempt number, starting at 1 for the first retry.
 * @returns The delay in milliseconds.
 */
export function retryDelayMs(attempt: number): number {
  return 150 * Math.pow(2, attempt - 1);
}
